import LocationMapPreview from '@app/src/components/Map/LocationMapPreview'
import TextField from '@app/src/components/Ui/TextField'
import type { CoordinateLocation } from '@app/src/types/resourceExplorer'
import { geocode, suggest } from '@app/src/utils/ArcGisApi'
import { isValidCoordinate, parseCoordinates } from '@app/src/utils/coordinateParser'
import { Autocomplete, Box, Button, Stack, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useIntl } from 'react-intl'

interface LocationSuggestion {
  magicKey: string
  text: string
}

interface CoordinateInputProps {
  value: CoordinateLocation
  onChange: (value: CoordinateLocation) => void
  onRemove?: () => void
  disabled?: boolean
  index?: number
  hasError?: boolean
  error?: string
}

const CoordinateInput: React.FC<CoordinateInputProps> = ({
  value,
  onChange,
  onRemove,
  disabled,
  index,
  hasError,
  error,
}) => {
  const { formatMessage } = useIntl()
  const [searchResults, setSearchResults] = useState<LocationSuggestion[]>([])
  const [search, setSearch] = useState('')
  const [showMap, setShowMap] = useState(false)
  const [combinedInput, setCombinedInput] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)

  const fetchSuggestions = useDebouncedCallback(async () => {
    try {
      const result = await suggest(search)
      setSearchResults(result?.suggestions?.length ? result.suggestions : [])
    } catch {
      setSearchResults([])
    }
  }, 200)

  useEffect(() => {
    if (search.length) {
      fetchSuggestions()
    } else {
      setSearchResults([])
    }
  }, [search])

  const handleAddressSelect = async (_event: React.SyntheticEvent, selected: string | LocationSuggestion | null) => {
    if (!selected || typeof selected === 'string') return
    try {
      const result = await geocode(selected.magicKey)
      if (result?.candidates?.length) {
        const candidate = result.candidates[0]
        onChange({
          ...value,
          latitude: candidate.location.y,
          longitude: candidate.location.x,
        })
      }
    } catch {
      // geocode failed, user can still enter manually
    }
  }

  const hasValidCoordinates =
    value.latitude !== undefined && value.longitude !== undefined && isValidCoordinate(value.latitude, value.longitude)

  const handleCombinedInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    setCombinedInput(input)

    if (!input.trim()) {
      setParseError(null)
      return
    }

    const parsed = parseCoordinates(input)
    if (parsed) {
      onChange({
        ...value,
        latitude: parsed.latitude,
        longitude: parsed.longitude,
      })
      setParseError(null)
    } else {
      setParseError(formatMessage({ id: 'form.validation.invalidCoordinateFormat' }))
    }
  }

  return (
    <Stack spacing={2} sx={{ p: 2.5 }}>
      {index !== undefined && onRemove && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" fontWeight="medium">
            {formatMessage({ id: 'general.location' })} {index + 1}
          </Typography>
          <Button size="small" onClick={onRemove} disabled={disabled}>
            {formatMessage({ id: 'general.remove' })}
          </Button>
        </Box>
      )}

      {/* Address Search */}
      <Autocomplete
        freeSolo
        options={searchResults}
        getOptionLabel={option => (typeof option === 'string' ? option : option.text)}
        noOptionsText={formatMessage({ id: 'general.typeToSearch' })}
        onChange={handleAddressSelect}
        filterOptions={options => options}
        disabled={disabled}
        renderInput={params => (
          <TextField
            {...params}
            hoveringLabel
            label={formatMessage({ id: 'general.searchAddress' })}
            placeholder={formatMessage({ id: 'general.searchAddressPlaceholder' })}
            onChange={e => setSearch(e.target.value)}
            size="small"
          />
        )}
      />

      {/* Convert Coordinates */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {formatMessage({ id: 'general.convertCoordinates' })}
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder={formatMessage({ id: 'general.convertCoordinatesPlaceholder' })}
          value={combinedInput}
          onChange={handleCombinedInputChange}
          error={!!parseError}
          helperText={parseError || formatMessage({ id: 'general.convertCoordinatesHelper' })}
          disabled={disabled}
          FormHelperTextProps={{ sx: { ml: 0 } }}
        />
      </Box>

      {/* Actual Answer Fields */}
      <Stack spacing={2}>
        <Box display="flex" gap={2} alignItems="flex-start">
          <TextField
            hoveringLabel
            label={formatMessage({ id: 'general.latitude' })}
            placeholder={formatMessage({ id: 'general.latitudePlaceholder' })}
            type="number"
            value={value.latitude ?? ''}
            onChange={e => {
              const input = e.target.value
              const parsed = input === '' ? undefined : parseFloat(input)
              onChange({ ...value, latitude: parsed })
            }}
            disabled={disabled}
            required
            fullWidth
            inputProps={{ min: -90, max: 90, step: 'any' }}
            error={hasError && (value.latitude === undefined || value.latitude === null)}
            helperText={hasError && (value.latitude === undefined || value.latitude === null) ? error : undefined}
          />
          <TextField
            hoveringLabel
            label={formatMessage({ id: 'general.longitude' })}
            placeholder={formatMessage({ id: 'general.longitudePlaceholder' })}
            type="number"
            value={value.longitude ?? ''}
            onChange={e => {
              const input = e.target.value
              const parsed = input === '' ? undefined : parseFloat(input)
              onChange({ ...value, longitude: parsed })
            }}
            disabled={disabled}
            required
            fullWidth
            inputProps={{ min: -180, max: 180, step: 'any' }}
            error={hasError && (value.longitude === undefined || value.longitude === null)}
            helperText={hasError && (value.longitude === undefined || value.longitude === null) ? error : undefined}
          />
          {hasValidCoordinates && (
            <Button
              size="small"
              onClick={() => setShowMap(!showMap)}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0, mt: '34px' }}
            >
              {showMap ? formatMessage({ id: 'general.hideMap' }) : formatMessage({ id: 'general.showOnMap' })}
            </Button>
          )}
        </Box>

        {showMap && hasValidCoordinates && value.latitude !== undefined && value.longitude !== undefined && (
          <LocationMapPreview latitude={value.latitude} longitude={value.longitude} />
        )}

        <TextField
          hoveringLabel
          label={formatMessage({ id: 'general.locationDescription' })}
          placeholder={formatMessage({ id: 'general.locationDescriptionPlaceholder' })}
          value={value.description || ''}
          onChange={e => onChange({ ...value, description: e.target.value })}
          disabled={disabled}
          required
          multiline
          rows={2}
          inputProps={{ maxLength: 50 }}
          helperText={
            hasError && !value.description ? error : formatMessage({ id: 'general.locationDescriptionHelper' })
          }
          error={hasError && !value.description}
          FormHelperTextProps={{ sx: { ml: 0 } }}
        />
      </Stack>
    </Stack>
  )
}

export default CoordinateInput
